
  import { Component, Prop, Vue } from 'vue-property-decorator'
  import { FormViewerLegacy as FormViewer } from '@parantion/components'
  import {
    QBtn,
    QCard,
    QCardActions,
    QCardSection,
    QCarousel,
    QCarouselSlide,
    QChip,
    QLinearProgress,
    QTooltip
  } from 'quasar'
  import TaskCard from '../components/TaskCard.vue'
  import TaskList from '../components/TaskList.vue'
  import anime from 'animejs'
  import Task, { TaskProgress } from '../models/Task'

  enum AnimationStatus {
    CLOSED_TO_TRAY, ANIMATING_TO_OPEN, ANIMATING_TO_TRAY, OPEN
  }

  function timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  @Component({
    components: {
      QLinearProgress,
      TaskCard,
      TaskList,
      QCarouselSlide,
      QTooltip,
      QCarousel,
      FormViewer,
      QChip,
      QCardActions,
      QCard,
      QCardSection,
      QBtn
    }
  })

  export default class Tasks extends Vue {
    @Prop({ default: false }) showAll: boolean
    @Prop({ default: false }) isMobile: boolean

    loading = false
    animating = false
    status: AnimationStatus = AnimationStatus.CLOSED_TO_TRAY
    windowHeight = 0
    windowObjectReference
    labelWelcome = this.$store.state.directus.config.label_welcome

    get sideTasksVisible() {
      return this.status === AnimationStatus.CLOSED_TO_TRAY
    }

    get fullTasksVisible() {
      return this.status !== AnimationStatus.CLOSED_TO_TRAY
    }

    get allTasks(): Task[] {
      function compare(a, b) {
        if (a['field'].frm_date_end < b['field'].frm_date_end) return 1

        if (a['field'].frm_date_end > b['field'].frm_date_end) return -1

        return 0
      }

      const unsortedTasks = (this.$store.state.tasks.tasks).slice().sort(compare)
      let sortedTasks = []
      const today = new Date().getTime()

      // Als de eind-datum verstreken is, mogen ze niet worden getoond bij de Openstaande vragenlijsten.
      sortedTasks = sortedTasks.concat(unsortedTasks.filter(t => t.field.progress === TaskProgress.INVITE && (new Date(t.field.frm_date_end).getTime()) > today))
      sortedTasks = sortedTasks.concat(unsortedTasks.filter(t => t.field.progress === TaskProgress.REMIND && (new Date(t.field.frm_date_end).getTime()) > today))
      sortedTasks = sortedTasks.concat(unsortedTasks.filter(t => t.field.progress === TaskProgress.BUSY && (new Date(t.field.frm_date_end).getTime()) > today))

      sortedTasks = sortedTasks.concat(unsortedTasks.filter(t => t.field.progress === TaskProgress.DONE))
      return (sortedTasks) // set latest to top
    }

    get adjustedTasks(): Task[] {
      const amount = (this.windowHeight - 380) / 71
      return this.allTasks.slice(0, amount)
    }

    get tasks() {
      if (this.status === AnimationStatus.ANIMATING_TO_OPEN || this.status === AnimationStatus.OPEN) {
        return this.allTasks
      } else {
        return this.adjustedTasks
      }
    }

    get openTasks() {
      const currDate = new Date(Date.now())
      const allowedStatuses = [TaskProgress.INVITE, TaskProgress.BUSY, TaskProgress.REMIND]
      const filterFunctie = (t) => {
        return allowedStatuses.includes(t.field['progress']) &&
          new Date(t['field'].frm_date_end) >= currDate &&
          (t.field['frm_status'] === '1' || t.field['frm_status'] === 1)
      }

      const filtered = this.sideTasksVisible && !this.showAll ? this.adjustedTasks.filter(filterFunctie) : this.allTasks.filter(filterFunctie)
      const length = this.allTasks.filter(t =>
        allowedStatuses.includes(t.field['progress']) &&
        new Date(t['field'].frm_date_end) >= currDate &&
        (t.field['frm_status'] === '1' || t.field['frm_status'] === 1)
      ).length
      return {
        length,
        tasks: filtered
      }
    }

    get completedTasks() {
      const filterByDone = t => {
        return t['field'].progress === 'progress_done' ||
          (t['field']['frm_status'] === 0 || new Date(t['field'].frm_date_end) < new Date(Date.now()))
      }

      const filtered = this.sideTasksVisible && !this.showAll ? this.adjustedTasks.filter(filterByDone) : this.allTasks.filter(filterByDone)
      const length = this.allTasks.filter(t =>
        t['field'].progress === 'progress_done' ||
        (t['field']['frm_status'] === 0 || new Date(t['field'].frm_date_end) < new Date(Date.now()))).length
      return {
        length, tasks: filtered
      }
    }

    get fullTaskView() {
      return this.$q.platform.is.mobile
    }

    get showVisibilityStyle() {
      return { opacity: this.fullTasksVisible || this.fullTaskView ? 1 : 0, zIndex: this.fullTasksVisible ? '2' : '-1' }
    }

    // Life cycles

    created() {
      this.getTasks()
    }

    mounted() {
      this.$nextTick(() => {
        window.addEventListener('resize', this.innerHeightListener)
        this.innerHeightListener()
      })
    }

    beforeDestroy() {
      window.removeEventListener('resize', this.innerHeightListener)
    }

    // Methods

    innerHeightListener() {
      this.windowHeight = window.innerHeight
    }

    openFormViewerTab(task: Task) {
      const link = task.link
      if (this.windowObjectReference) {
        const vm = this
        if (this.windowObjectReference.closed) {
          this.logoutAndOpen(vm, link)
        } else {
          // @ts-ignore
          vm.$q.dialog({
            title: 'Confirm',
            message: 'Tab nog open. Overschrijven',
            ok: 'Agree',
            cancel: 'Disagree'
          }).onOk(() => {
            this.logoutAndOpen(vm, link)
          })
        }
      } else {
        this.windowObjectReference = window.open(link, 'easionpro', 'menubar=yes,location=yes,resizable=yes,' +
          'scrollbars=yes,status=yes')
      }
    }

    logoutAndOpen(vm, link) {
      if (vm.windowObjectReference){
        vm.windowObjectReference.close()
      }
      vm.windowObjectReference = window.open(link + '&logout=1', 'easionpro', 'menubar=yes,location=yes,resizable=yes,' +
        'scrollbars=yes,status=yes')
    }

    closed() {
      this.getTasks()
    }

    animeate(status: AnimationStatus) {
      return new Promise(resolve => {
        const promises = []

        const els = document.querySelectorAll('#full-task-view .task-card-container')
        // CM-845: Empty list did not close so added a check for this
        if (els.length === 0) {
          resolve()
          return
        }
        for (const element of els) {
          const relative = document.querySelector(`#side-tasks [data-task="${element.getAttribute('data-task')}"]`)

          const elementBounding = element.getBoundingClientRect()
          const relativeBounding = relative.getBoundingClientRect()

          const relativeLeft = relativeBounding.left
          const relativeTop = relativeBounding.top

          const elementLeft = elementBounding.left
          const elementTop = elementBounding.top

          const positionTop = relativeTop - elementTop
          const positionLeft = relativeLeft - elementLeft

          const visibleInTray = !!this.adjustedTasks.find(t => t.th === element.getAttribute('data-task'))

          if (status === AnimationStatus.ANIMATING_TO_TRAY) {
            // @ts-ignore
            promises.push(new Promise((resolve1) => {
              anime({
                targets: element,
                translateX: positionLeft,
                translateY: positionTop,
                delay: Math.random() * 300,
                duration: 500,
                easing: 'easeInOutQuad',
                complete: () => {
                  resolve1(element)
                }
              })
            }))
          } else {
            anime({
              targets: element,
              translateX: positionLeft,
              translateY: positionTop,
              opacity: visibleInTray ? 1 : 0,
              duration: 0
            })

            setTimeout(() => {
              anime({
                targets: element,
                translateX: 0,
                translateY: 0,
                delay: Math.random() * 500,
                opacity: 1,
                duration: 800,
                easing: 'easeInOutQuad',
                complete: resolve
              })
            }, 0)
          }

          Promise.all(promises).then(elements => {
            setTimeout(() => {
              elements.forEach(revertElement => {
                anime({
                  targets: revertElement,
                  translateX: 0,
                  translateY: 0,
                  duration: 0
                })
              })
            }, 400)
            resolve()
          })
        }
      })
    }

    async getTasks() {
      this.loading = true
      try {
        await this.$store.dispatch('tasks/retrieveTasks')
      } catch (e) {
        // something went wrong
      } finally {
        this.loading = false
      }
    }

    async expand() {
      const OPEN_TIME_MS = 400
      const CLOSE_OFFSET_MS = 100
      if (this.status === AnimationStatus.CLOSED_TO_TRAY) {
        this.$emit('side-view', true)
        // We're going to open, lets wait till the tray is open
        await timeout(OPEN_TIME_MS)
        this.status = AnimationStatus.ANIMATING_TO_OPEN

        this.$nextTick(() => {
          this.animeate(this.status).then(() => {
            this.status = AnimationStatus.OPEN
          })
        })
      } else if (this.status === AnimationStatus.OPEN) {
        this.status = AnimationStatus.ANIMATING_TO_TRAY
        this.$nextTick(() => {
          this.animeate(this.status).then(async () => {
            await timeout(CLOSE_OFFSET_MS)
            this.$emit('side-view', false)
            this.status = AnimationStatus.CLOSED_TO_TRAY
          })
        })
      }
    }

    hasHiddenTasks(type) {
      switch (type) {
        case 'open':
          return this.openTasks.length > this.openTasks.tasks.length
        case 'done':
          return this.completedTasks.length > this.completedTasks.tasks.length
      }
    }
  }
